import type { IWFVersionDto } from "@kuika/kuika-cl-model/src/dtos/workflow.dto";
import type { AxiosResponse } from "axios";
import { useCallback, useEffect, useState } from "react";
import { WorkFlowService } from "../../../../services/workflow-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import VersionCompareModal from "./version-compare-modal";
import VersionDiagramModal from "./version-diagram-modal";
import { IVersionRow, toVersionRows } from "./version-history";
import VersionHistoryTable from "./version-history-table";

/**
 * Version History tab: owns the data for the table and turns its callbacks into service
 * calls. The table itself is presentational and the two modals are self-closing
 * controllers, so everything that talks to the API lives here.
 */

export interface IVersionHistoryTabProps {}

const VersionHistoryTab = ({ sd }: IVersionHistoryTabProps & IWithSDProps) => {
  const workflowId: string | undefined = sd.selectedWf?.id;
  const [versions, setVersions] = useState<IWFVersionDto[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [diagramVersion, setDiagramVersion] = useState<IVersionRow | undefined>(undefined);
  const [compareSelection, setCompareSelection] = useState<IVersionRow[] | undefined>(undefined);

  const rows: IVersionRow[] = toVersionRows(versions);

  const load = useCallback(
    async (showSpinner: boolean): Promise<void> => {
      if (!workflowId) {
        setVersions([]);
        setIsLoading(false);
        return;
      }
      if (showSpinner) setIsLoading(true);
      const response: AxiosResponse<IWFVersionDto[]> = await WorkFlowService.GetVersions(workflowId);
      // The service reports a failed request by RESOLVING with the error object (and has
      // already toasted it), so neither the response nor its data is guaranteed.
      const loaded: IWFVersionDto[] | undefined = response?.data;
      setVersions(Array.isArray(loaded) ? loaded : []);
      setIsLoading(false);
    },
    [workflowId]
  );

  // Reload when the tab mounts and whenever the open process changes; a stale list from the
  // previously open process must never be shown against the new one.
  useEffect((): void => {
    setSelectedIds([]);
    setSearch("");
    void load(true);
  }, [load]);

  /**
   * Applies an edit locally first so the cell shows the new value immediately, then persists.
   * `UpdateVersion` carries both fields, so the one that is not being edited has to be sent
   * as it currently stands. A rejected save re-reads the list rather than leaving the
   * optimistic value on screen.
   */
  const saveVersion = async (row: IVersionRow, name: string, description: string): Promise<void> => {
    setVersions((previous: IWFVersionDto[]): IWFVersionDto[] =>
      previous.map((version: IWFVersionDto): IWFVersionDto =>
        version.id === row.id ? { ...version, name, description } : version
      )
    );
    const saved: boolean = await WorkFlowService.UpdateVersion({ id: row.id, name, description });
    if (!saved) await load(false);
  };

  const handleRename = (row: IVersionRow, name: string): void => {
    void saveVersion(row, name, row.description);
  };

  const handleDescribe = (row: IVersionRow, description: string): void => {
    void saveVersion(row, row.name, description);
  };

  const setActive = async (ids: readonly string[], active: boolean): Promise<void> => {
    const results: boolean[] = await Promise.all(
      ids.map((id: string): Promise<boolean> =>
        active ? WorkFlowService.ActivateVersion(id) : WorkFlowService.DeactivateVersion(id)
      )
    );
    // Read the list back rather than trusting the toggles: the flag is the API's own
    // soft-delete, and a partially-applied bulk change must not be guessed at.
    await load(false);
    if (results.some((succeeded: boolean): boolean => succeeded)) setSelectedIds([]);
  };

  const handleActivate = (row: IVersionRow): void => {
    void setActive([row.id], true);
  };

  const handleDeactivate = (row: IVersionRow): void => {
    void setActive([row.id], false);
  };

  /** The toolbar's bulk deactivate; already-inactive rows in the selection have nothing to do. */
  const handleDeactivateMany = (ids: string[]): void => {
    const activeIds: string[] = rows
      .filter((row: IVersionRow): boolean => ids.includes(row.id) && row.isActive)
      .map((row: IVersionRow): string => row.id);
    if (activeIds.length === 0) return;
    void setActive(activeIds, false);
  };

  /** The toolbar's bulk activate; already-active rows in the selection have nothing to do. */
  const handleActivateMany = (ids: string[]): void => {
    const inactiveIds: string[] = rows
      .filter((row: IVersionRow): boolean => ids.includes(row.id) && !row.isActive)
      .map((row: IVersionRow): string => row.id);
    if (inactiveIds.length === 0) return;
    void setActive(inactiveIds, true);
  };

  const handleCompare = (ids: string[]): void => {
    const picked: IVersionRow[] = rows.filter((row: IVersionRow): boolean => ids.includes(row.id));
    setCompareSelection(picked);
  };

  return (
    <div className="kuika-process-editor__version-history" data-testid="processVersionHistoryTab">
      <VersionHistoryTable
        rows={rows}
        isLoading={isLoading}
        search={search}
        onSearchChange={setSearch}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        onRenameVersion={handleRename}
        onDescribeVersion={handleDescribe}
        onDeactivateVersions={handleDeactivateMany}
        onActivateVersions={handleActivateMany}
        onCompareVersions={handleCompare}
        onViewDiagram={setDiagramVersion}
        onActivateVersion={handleActivate}
        onDeactivateVersion={handleDeactivate}
      />
      <VersionDiagramModal version={diagramVersion} onClose={(): void => setDiagramVersion(undefined)} />
      <VersionCompareModal versions={compareSelection} onClose={(): void => setCompareSelection(undefined)} />
    </div>
  );
};

const versionHistoryTab = withSD<IVersionHistoryTabProps>(VersionHistoryTab);
export default versionHistoryTab;
